<template>
  <footer class="bg-gradient no-print z-10 flex md:justify-center">
    <div
      class="z-50 mx-auto w-full max-w-[90rem] flex-wrap justify-between p-4 px-4 pb-8 text-white md:px-6 lg:flex lg:flex-col lg:pt-[4.81rem]"
    >
      <div class="lg-flex-col lg:flex lg:gap-60">
        <div class="mb-16 mt-12 lg:mt-0 lg:flex lg:flex-col">
          <div class="mb-8 flex flex-col">
            <div>
              <a :href="getLandingPageUrl()">
                <img
                  src="@/assets/images/base/logos/diagu_footer.svg"
                  alt="getlabtest-logo"
                  class="mb-3.5 hidden lg:inline lg:w-[10.3rem]"
                />
                <img
                  src="@/assets/images/base/logos/diagu.svg"
                  alt="getlabtest-logo"
                  class="mb-3.5 w-[116px] lg:hidden"
                />
              </a>
            </div>

            <FooterSocialMedia :is-get-lab-test="false" />
          </div>

          <div class="flex flex-col">
            <div>
              <a :href="getLandingPageUrl()">
                <img
                  src="@/assets/images/base/logos/gt_footer.svg"
                  alt="getlabtest-logo"
                  class="mb-3.5 mt-4 hidden w-[343px] lg:inline"
                />
                <img
                  src="@/assets/images/base/logos/glt_dark_bold.svg"
                  alt="getlabtest-logo"
                  class="mb-3.5 w-[266px] lg:hidden"
                />
              </a>
            </div>

            <FooterSocialMedia :is-get-lab-test="true" />

            <FooterAddress class="mt-10 hidden md:block" />
          </div>
        </div>

        <div class="flex flex-col lg:flex-row lg:gap-32">
          <div class="mb-10">
            <p class="mb-6 text-h-m-7 text-white lg:text-22 lg:font-bold">
              {{ $t("who_we_are.footer.for_patient.header") }}
            </p>
            <div class="flex flex-col gap-1 lg:gap-4">
              <a
                v-for="(link, index) in patientLinks"
                :key="index"
                class="text-sm font-light text-white"
                :href="link.url"
                :target="link.blank ? '_blank' : '_self'"
              >
                {{ link.name }}
              </a>
            </div>
          </div>

          <div class="mb-10">
            <p class="h-m-7 mb-6 text-white lg:text-22 lg:font-bold">
              {{ $t("who_we_are.footer.for_providers.header") }}
            </p>
            <div class="flex flex-col gap-1 lg:gap-4">
              <a
                v-for="(link, index) in providersLinks"
                :key="index"
                class="text-sm font-light text-white"
                :href="link.url"
                :target="link.blank ? '_blank' : '_self'"
              >
                {{ link.name }}
              </a>
            </div>
          </div>

          <div class="mb-14">
            <p class="h-m-7 mb-6 text-white lg:text-22 lg:font-bold">
              {{ $t("who_we_are.footer.about.header") }}
            </p>
            <div class="flex flex-col gap-1 lg:gap-4">
              <a
                v-for="(link, index) in aboutLinks"
                :key="index"
                class="text-sm font-light text-white"
                :href="link.url"
              >
                {{ link.name }}
              </a>

              <a
                class="cursor-pointer text-sm font-light text-white"
                @click.prevent="manageCookies"
              >
                Manage cookies
              </a>
            </div>
          </div>
        </div>
      </div>

      <FooterAddress class="mb-10 md:hidden" />
      <div class="text-sm font-light text-white">
        &copy; {{ currentYear }} Diagu and GetLabTest. All Rights Reserved.
      </div>
    </div>
  </footer>
</template>

<script>
import FooterSocialMedia from "@/components/Core/FooterSocialMedia";
import landingPageMixin from "@/mixins/landingPageMixin";
import FooterAddress from "./FooterAddress.vue";
export default {
  name: "Footer",
  mixins: [landingPageMixin],
  data() {
    return {
      currentYear: new Date().getFullYear(),
    };
  },
  components: {
    FooterSocialMedia,
    FooterAddress,
  },
  computed: {
    patientLinks() {
      return [
        {
          name: this.$t("who_we_are.footer.for_patient.links.list_of_tests"),
          url: this.getLandingPageUrl("lab-test-list/tests"),
        },
        {
          name: this.$t("who_we_are.footer.for_patient.links.packages"),
          url: this.getLandingPageUrl("lab-test-list/packages"),
        },
        {
          name: this.$t("who_we_are.footer.for_patient.links.view_results"),
          url: "/cart/orders",
        },
        {
          name: this.$t(
            "who_we_are.footer.for_patient.links.collection_points"
          ),
          url: this.getLandingPageUrl("locations"),
        },
        {
          name: this.$t("who_we_are.footer.for_patient.links.faq_help_center"),
          url: "https://www.getlabtest.com/help/en/collections/7208853-for-patients",
          blank: true,
        },
        {
          name: this.$t(
            "who_we_are.footer.for_patient.links.patient_terms_of_use"
          ),
          url: this.getLandingPageUrl("patient-terms-of-use"),
        },
      ];
    },
    providersLinks() {
      return [
        {
          name: this.$t(
            "who_we_are.footer.for_providers.links.offer_for_providers"
          ),
          url: this.getLandingPageUrl("doctor"),
        },
        {
          name: this.$t(
            "who_we_are.footer.for_providers.links.faq_help_center"
          ),
          url: "https://www.getlabtest.com/help/en/collections/7208791-for-providers",
          blank: true,
        },
        {
          name: this.$t(
            "who_we_are.footer.for_providers.links.providers_terms_of_use"
          ),
          url: this.getLandingPageUrl("providers-terms-of-use"),
        },
      ];
    },
    aboutLinks() {
      return [
        {
          name: this.$t("who_we_are.footer.about.links.about_us"),
          url: this.getLandingPageUrl("about-us"),
        },
        {
          name: this.$t("who_we_are.footer.about.links.contact"),
          url: this.getLandingPageUrl("contact"),
        },
        {
          name: this.$t("who_we_are.footer.about.links.privacy_policy"),
          url: this.getLandingPageUrl("privacy-policy"),
        },
      ];
    },
  },
  methods: {
    manageCookies() {
      Cookiebot.show();
    },
  },
};
</script>
<style>
.bg-gradient {
  background: linear-gradient(153.52deg, #02208a 22.19%, #0e1760 85.07%);
}
</style>
